import { Auth } from 'aws-amplify';
import moment from 'moment';

import { saveCredentials } from '../actions/hub/HubHelperActions';
import { ddLogger, ddLoggerLevel } from '../logging';
import { FETCH_FAILED, INVALID_RESPONSE } from '../logging/errors';
import { store } from '../store/configureStore';
import { ROLE_STAFF } from '../util/constants';
import getAppVersion from '../util/getAppVersion';
import { getCurrentCredentials } from './awsAuth';

const sendRequest = async ({ url, method, payload, customErrorMsg, signRequest, reqOptions }) => {
  let response;

  try {
    const isPostOrPatch = method === 'POST' || method === 'PATCH';
    let requestOptions = {};

    if (signRequest) {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const state = store.getState();
      let { secret: secretAccessKey, accessKey: accessKeyId, sessionToken, expiration } = state?.login?.credentials;
      const isTokenExpired = moment(expiration).isBefore(moment());

      if (isTokenExpired || !secretAccessKey || !accessKeyId || !sessionToken) {
        const credentials = await getCurrentCredentials();
        ({ secretAccessKey, accessKeyId, sessionToken } = credentials);
        store.dispatch(saveCredentials({ credentials }));
      }

      if (idToken) {
        reqOptions.headers = {
          ...reqOptions.headers,
          authorization: `Bearer ${idToken}`,
        };
      } else {
        throw new Error('No ID token found');
      }

      if (state?.login?.role === ROLE_STAFF) {
        const { staffToken } = state?.login?.credentials;

        reqOptions.headers = {
          ...reqOptions.headers,
          stafftoken: staffToken,
        };
      }

      requestOptions = {
        ...reqOptions,
        method,
        region: 'ap-southeast-2',
        service: 'execute-api',
        body: isPostOrPatch ? JSON.stringify(payload) : undefined,
      };
    } else {
      payload.sourceSystem = 'Hub';
      requestOptions = {
        method,
        headers: isPostOrPatch ? { 'Content-Type': 'application/json' } : {},
        body: isPostOrPatch ? JSON.stringify(payload) : undefined,
      };
    }

    if (url && url.includes('api-v2.clueylearn.com') && requestOptions.headers) {
      requestOptions.headers['x-cluey-app-version'] = getAppVersion();
      requestOptions.headers['x-cluey-source-system'] = 'Customer Hub';
    }

    response = await fetch(url, requestOptions);

    const customError = new Error(customErrorMsg);

    if (!response) {
      ddLogger({
        level: ddLoggerLevel.ERROR,
        label: INVALID_RESPONSE.message,
        data: { url, ...payload, response },
        error: new Error(INVALID_RESPONSE.message),
      });
      throw customError;
    }
    if (!response.ok) {
      const jsonResponse = await response.json();
      const error = new Error(jsonResponse.error);
      ddLogger({
        level: ddLoggerLevel.ERROR,
        label: INVALID_RESPONSE.message,
        data: { url, ...payload, response },
        error,
      });
      throw error;
    }
    if (response.status === 500) {
      throw customError;
    }

    return response;
  } catch (error) {
    console.warn(error);
    const { message } = error;
    if (message !== customErrorMsg) {
      ddLogger({
        level: ddLoggerLevel.ERROR,
        label: FETCH_FAILED.message,
        data: { url, ...payload, error },
        error: new Error(FETCH_FAILED.message),
      });
    }
    throw error;
  }
};

export const sendGetRequest = async ({ url, payload, customErrorMsg, signRequest = false, reqOptions = {} }) => {
  return sendRequest({
    url,
    method: 'GET',
    payload,
    customErrorMsg,
    signRequest,
    reqOptions,
  });
};

export const sendPostRequest = async ({ url, payload, customErrorMsg, signRequest = false, reqOptions = {} }) => {
  return sendRequest({
    url,
    method: 'POST',
    payload,
    customErrorMsg,
    signRequest,
    reqOptions,
  });
};

export const sendPatchRequest = async ({ url, payload, customErrorMsg, signRequest = false, reqOptions = {} }) => {
  return sendRequest({
    url,
    method: 'PATCH',
    payload,
    customErrorMsg,
    signRequest,
    reqOptions,
  });
};
